import React, { createContext, useCallback, useEffect, useMemo } from 'react';
import { useRouteLoaderData, useLocation } from 'react-router';
import { ROUTE_IDS } from '@/common/auth-routes';
import { logout as performLogout } from '@/common/auth-utils';
import { getTenantFromPathString } from '@/common/tenant-utils';
import type { AuthLoaderData } from '@/router/loaders/auth-loader';
import { identifyPosthogUser, resetPosthogUser } from '@/telemetry/posthog';
import type { AuthContextType } from '@/types';
import { logError } from '@/utils/error-logger';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const loaderData = useRouteLoaderData(ROUTE_IDS.TENANT_ROOT) as
    | AuthLoaderData
    | undefined;

  const location = useLocation();

  // Derive tenantSlug from current location - updates reactively on navigation
  const tenantSlug = useMemo(
    () => getTenantFromPathString(location.pathname),
    [location.pathname]
  );

  const isAuthenticated = loaderData?.authenticated ?? false;
  const user = loaderData?.user ?? null;

  useEffect(() => {
    if (isAuthenticated && user) {
      identifyPosthogUser({ id: user.id, tenantSlug: tenantSlug ?? undefined });
    }
  }, [isAuthenticated, user, tenantSlug]);

  const logout = useCallback(async () => {
    resetPosthogUser();

    try {
      // performLogout handles SAML logout flow and redirect
      // tenantSlug will update automatically from the new URL
      await performLogout();
    } catch (error) {
      logError(error, 'auth:logout');
      // Error handling is done in performLogout (falls back to page reload)
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const value: AuthContextType = useMemo(
    () => ({
      user,
      tenantSlug,
      isAuthenticated,
      logout,
    }),
    [user, tenantSlug, isAuthenticated, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { AuthContext };
